import { CatalogStatus, Product, ProductAttributeDefinition, ProductAttributeFieldType, ProductCategory } from '../types/Product';
import { normalizeCatalogAttachments } from '../../catalog/catalogAttachments';
import { apiRequest } from '../../../shared/services/apiClient';

export interface ProductListQuery {
  q?: string;
  categoryId?: string;
  categoryIds?: string[];
  status?: CatalogStatus;
  sortBy?: 'name' | 'salePrice' | 'status' | 'createdAt' | 'updatedAt';
  sortDir?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
  attributes?: Record<string, string>;
}

export interface CreateProductRequest {
  name?: string;
  displayName?: string;
  description?: string;
  categoryId?: string;
  code?: string;
  barcode?: string;
  costPrice?: number;
  markupPercent?: number;
  salePrice?: number;
  taxPercent?: number;
  stockQuantity?: number;
  unit?: string;
  status?: CatalogStatus;
  attributes?: Record<string, string | null>;
}

export interface UpdateProductRequest extends CreateProductRequest {
  clearDisplayName?: boolean;
  clearCode?: boolean;
  clearBarcode?: boolean;
  clearCostPrice?: boolean;
  clearMarkupPercent?: boolean;
  clearSalePrice?: boolean;
  clearTaxPercent?: boolean;
  clearUnit?: boolean;
}

export interface CreateProductCategoryRequest {
  name?: string;
  parentId?: string;
  defaultMarkupPercent?: number;
  sortOrder?: number;
}

export interface UpdateProductCategoryRequest {
  name?: string;
  parentId?: string;
  clearParent?: boolean;
  defaultMarkupPercent?: number;
  clearDefaultMarkupPercent?: boolean;
  sortOrder?: number;
}

export interface CreateProductAttributeRequest {
  name?: string;
  key?: string;
  fieldType?: ProductAttributeFieldType;
  isRequired?: boolean;
  options?: string[];
  sortOrder?: number;
}

export interface UpdateProductAttributeRequest {
  name?: string;
  key?: string;
  fieldType?: ProductAttributeFieldType;
  isRequired?: boolean;
  options?: string[];
  clearOptions?: boolean;
  sortOrder?: number;
}

interface PagedResult<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
  hasNext: boolean;
}

const statuses: CatalogStatus[] = ['active', 'archived'];
const fieldTypes: ProductAttributeFieldType[] = ['text', 'number', 'date', 'select', 'yesNo'];

const normalizeFieldType = (value: unknown): ProductAttributeFieldType => {
  const normalized = String(value ?? '').trim();
  const camel = normalized.charAt(0).toLowerCase() + normalized.slice(1);
  return fieldTypes.includes(camel as ProductAttributeFieldType) ? camel as ProductAttributeFieldType : 'text';
};

const normalizeAttributes = (value: unknown): Record<string, string> => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>)
      .map(([key, item]) => [key, String(item ?? '').trim()] as const)
      .filter(([, item]) => item.length > 0),
  );
};

const normalizeAttribute = (value: any): ProductAttributeDefinition => ({
  id: String(value?.id ?? ''),
  categoryId: String(value?.categoryId ?? ''),
  name: String(value?.name ?? ''),
  key: String(value?.key ?? ''),
  fieldType: normalizeFieldType(value?.fieldType),
  isRequired: Boolean(value?.isRequired),
  options: Array.isArray(value?.options) ? value.options.map((item: unknown) => String(item ?? '').trim()).filter(Boolean) : [],
  sortOrder: optionalNumber(value?.sortOrder) ?? 0,
  inherited: Boolean(value?.inherited),
  sourceCategoryName: optionalText(value?.sourceCategoryName),
  createdAt: value?.createdAt ? String(value.createdAt) : undefined,
  updatedAt: value?.updatedAt ? String(value.updatedAt) : undefined,
});

const normalizeStatus = (value: unknown): CatalogStatus => {
  const normalized = String(value ?? '').toLowerCase();
  return statuses.includes(normalized as CatalogStatus) ? normalized as CatalogStatus : 'active';
};

const optionalNumber = (value: unknown): number | undefined => {
  if (value === null || value === undefined || value === '') return undefined;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const optionalText = (value: unknown): string | undefined => {
  const text = String(value ?? '').trim();
  return text || undefined;
};

const normalizeProduct = (value: any): Product => ({
  id: String(value?.id ?? ''),
  name: String(value?.name ?? ''),
  displayName: optionalText(value?.displayName),
  description: String(value?.description ?? ''),
  categoryId: String(value?.categoryId ?? ''),
  categoryName: String(value?.categoryName ?? ''),
  code: optionalText(value?.code),
  barcode: optionalText(value?.barcode),
  costPrice: optionalNumber(value?.costPrice),
  markupPercent: optionalNumber(value?.markupPercent),
  salePrice: optionalNumber(value?.salePrice),
  taxPercent: optionalNumber(value?.taxPercent),
  stockQuantity: optionalNumber(value?.stockQuantity) ?? 0,
  unit: optionalText(value?.unit),
  imageUrl: optionalText(value?.imageUrl),
  attachments: normalizeCatalogAttachments(value?.attachments),
  attributes: normalizeAttributes(value?.attributes),
  status: normalizeStatus(value?.status),
  createdAt: value?.createdAt ? String(value.createdAt) : undefined,
  updatedAt: value?.updatedAt ? String(value.updatedAt) : undefined,
});

const normalizeCategory = (value: any): ProductCategory => ({
  id: String(value?.id ?? ''),
  name: String(value?.name ?? ''),
  parentId: optionalText(value?.parentId),
  defaultMarkupPercent: optionalNumber(value?.defaultMarkupPercent),
  sortOrder: optionalNumber(value?.sortOrder) ?? 0,
  createdAt: value?.createdAt ? String(value.createdAt) : undefined,
  updatedAt: value?.updatedAt ? String(value.updatedAt) : undefined,
});

const pickItems = (payload: any): any[] => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.items)) return payload.data.items;
  return [];
};

const pickSingle = (payload: any): any => {
  if (!payload || typeof payload !== 'object') return null;
  if (payload.data && typeof payload.data === 'object' && !Array.isArray(payload.data) && !payload.id) {
    return payload.data;
  }
  return payload;
};

const buildQuery = (path: string, query?: Record<string, string | number | string[] | undefined>): string => {
  if (!query) return path;
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined || value === '') return;
    if (Array.isArray(value)) {
      value.forEach(item => {
        if (item) params.append(key, item);
      });
      return;
    }
    params.set(key, String(value));
  });
  const queryString = params.toString();
  return queryString ? `${path}?${queryString}` : path;
};

const withAttributes = (path: string, attributes?: Record<string, string>) => {
  if (!attributes) return path;
  const params = new URLSearchParams();
  Object.entries(attributes).forEach(([id, value]) => {
    if (!value.trim()) return;
    params.set(`attributes[${id}]`, value.trim());
  });
  const extra = params.toString();
  if (!extra) return path;
  return path.includes('?') ? `${path}&${extra}` : `${path}?${extra}`;
};

const notFoundToNull = async <T>(work: () => Promise<T>): Promise<T | null> => {
  try {
    return await work();
  } catch (error) {
    if (error instanceof Error && /404|not found/i.test(error.message)) {
      return null;
    }
    throw error;
  }
};

export const productService = {
  getProducts: async (query?: ProductListQuery): Promise<Product[]> => {
    const payload = await apiRequest<PagedResult<any> | any[]>(withAttributes(buildQuery('/api/v1/products', {
      q: query?.q,
      categoryId: query?.categoryIds?.length ? undefined : query?.categoryId,
      categoryIds: query?.categoryIds?.length ? query.categoryIds : undefined,
      status: query?.status,
      sortBy: query?.sortBy,
      sortDir: query?.sortDir,
      page: query?.page,
      pageSize: query?.pageSize,
    }), query?.attributes));
    return pickItems(payload).map(normalizeProduct);
  },

  getProductById: async (id: string): Promise<Product | null> =>
    notFoundToNull(async () => {
      const item = pickSingle(await apiRequest<any>(`/api/v1/products/${id}`));
      return item ? normalizeProduct(item) : null;
    }),

  createProduct: async (request: CreateProductRequest): Promise<Product | null> => {
    const item = pickSingle(await apiRequest<any>('/api/v1/products', {
      method: 'POST',
      body: JSON.stringify(request),
    }));
    return item ? normalizeProduct(item) : null;
  },

  updateProduct: async (id: string, request: UpdateProductRequest): Promise<Product | null> =>
    notFoundToNull(async () => {
      const item = pickSingle(await apiRequest<any>(`/api/v1/products/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(request),
      }));
      return item ? normalizeProduct(item) : null;
    }),

  archiveProduct: async (id: string): Promise<void> => {
    await apiRequest(`/api/v1/products/${id}`, { method: 'DELETE' });
  },

  updateProductStatus: async (id: string, status: CatalogStatus): Promise<Product | null> =>
    notFoundToNull(async () => {
      const item = pickSingle(await apiRequest<any>(`/api/v1/products/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      }));
      return item ? normalizeProduct(item) : null;
    }),

  uploadProductImage: async (id: string, file: File): Promise<Product | null> =>
    notFoundToNull(async () => {
      const formData = new FormData();
      formData.append('file', file);
      const item = pickSingle(await apiRequest<any>(`/api/v1/products/${id}/image`, {
        method: 'POST',
        body: formData,
      }));
      return item ? normalizeProduct(item) : null;
    }),

  removeProductImage: async (id: string): Promise<Product | null> =>
    notFoundToNull(async () => {
      const item = pickSingle(await apiRequest<any>(`/api/v1/products/${id}/image`, {
        method: 'DELETE',
      }));
      return item ? normalizeProduct(item) : null;
    }),

  uploadProductAttachment: async (id: string, file: File, setAsPrimary = false): Promise<Product | null> =>
    notFoundToNull(async () => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('setAsPrimary', setAsPrimary ? 'true' : 'false');
      const item = pickSingle(await apiRequest<any>(`/api/v1/products/${id}/attachments`, {
        method: 'POST',
        body: formData,
      }));
      return item ? normalizeProduct(item) : null;
    }),

  removeProductAttachment: async (id: string, attachmentId: string): Promise<Product | null> =>
    notFoundToNull(async () => {
      const item = pickSingle(await apiRequest<any>(`/api/v1/products/${id}/attachments/${attachmentId}`, {
        method: 'DELETE',
      }));
      return item ? normalizeProduct(item) : null;
    }),

  setProductPrimaryImage: async (id: string, attachmentId: string): Promise<Product | null> =>
    notFoundToNull(async () => {
      const item = pickSingle(await apiRequest<any>(`/api/v1/products/${id}/attachments/${attachmentId}/primary`, {
        method: 'POST',
      }));
      return item ? normalizeProduct(item) : null;
    }),

  getCategories: async (query?: { q?: string; parentId?: string }): Promise<ProductCategory[]> => {
    const payload = await apiRequest<any>(buildQuery('/api/v1/product-categories', {
      q: query?.q,
      parentId: query?.parentId,
    }));
    return pickItems(payload).map(normalizeCategory);
  },

  getCategoryById: async (id: string): Promise<ProductCategory | null> =>
    notFoundToNull(async () => {
      const item = pickSingle(await apiRequest<any>(`/api/v1/product-categories/${id}`));
      return item ? normalizeCategory(item) : null;
    }),

  createCategory: async (request: CreateProductCategoryRequest): Promise<ProductCategory | null> => {
    const item = pickSingle(await apiRequest<any>('/api/v1/product-categories', {
      method: 'POST',
      body: JSON.stringify(request),
    }));
    return item ? normalizeCategory(item) : null;
  },

  updateCategory: async (id: string, request: UpdateProductCategoryRequest): Promise<ProductCategory | null> =>
    notFoundToNull(async () => {
      const item = pickSingle(await apiRequest<any>(`/api/v1/product-categories/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(request),
      }));
      return item ? normalizeCategory(item) : null;
    }),

  deleteCategory: async (id: string): Promise<void> => {
    await apiRequest(`/api/v1/product-categories/${id}`, { method: 'DELETE' });
  },

  getAttributes: async (categoryId: string, includeInherited = false): Promise<ProductAttributeDefinition[]> => {
    const payload = await apiRequest<any>(buildQuery(`/api/v1/product-categories/${categoryId}/attributes`, {
      includeInherited: includeInherited ? 'true' : undefined,
    }));
    return pickItems(payload).map(normalizeAttribute);
  },

  createAttribute: async (categoryId: string, request: CreateProductAttributeRequest): Promise<ProductAttributeDefinition | null> => {
    const item = pickSingle(await apiRequest<any>(`/api/v1/product-categories/${categoryId}/attributes`, {
      method: 'POST',
      body: JSON.stringify(request),
    }));
    return item ? normalizeAttribute(item) : null;
  },

  updateAttribute: async (categoryId: string, attributeId: string, request: UpdateProductAttributeRequest): Promise<ProductAttributeDefinition | null> =>
    notFoundToNull(async () => {
      const item = pickSingle(await apiRequest<any>(`/api/v1/product-categories/${categoryId}/attributes/${attributeId}`, {
        method: 'PATCH',
        body: JSON.stringify(request),
      }));
      return item ? normalizeAttribute(item) : null;
    }),

  deleteAttribute: async (categoryId: string, attributeId: string): Promise<void> => {
    await apiRequest(`/api/v1/product-categories/${categoryId}/attributes/${attributeId}`, { method: 'DELETE' });
  },
};

export const getProducts = productService.getProducts;
export const getProductById = productService.getProductById;
export const createProduct = productService.createProduct;
export const updateProduct = productService.updateProduct;
export const archiveProduct = productService.archiveProduct;
export const updateProductStatus = productService.updateProductStatus;
export const uploadProductImage = productService.uploadProductImage;
export const removeProductImage = productService.removeProductImage;
export const uploadProductAttachment = productService.uploadProductAttachment;
export const removeProductAttachment = productService.removeProductAttachment;
export const setProductPrimaryImage = productService.setProductPrimaryImage;
export const getProductCategories = productService.getCategories;
export const getCategoryAttributes = productService.getAttributes;
export const createCategoryAttribute = productService.createAttribute;
export const updateCategoryAttribute = productService.updateAttribute;
export const deleteCategoryAttribute = productService.deleteAttribute;
